import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BrouillonRegistrePolice,
  InscriptionRegistrePolice,
  MentionsRegistrePolice,
} from '@artosera/shared';
import { api, requeteMultipart, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { Pagination } from './types.js';

type DatesJson<T> = T extends Date
  ? string
  : T extends object
    ? { [K in keyof T]: DatesJson<T[K]> }
    : T;
export type InscriptionPresentee = DatesJson<InscriptionRegistrePolice> & { id: string };
export interface PropositionPresentee {
  id: string;
  etat: 'en_attente' | 'inscrite' | 'ecartee';
  brouillon: DatesJson<BrouillonRegistrePolice>;
  mentionsManquantes: string[];
  inscriptionNumero?: number;
  motifEcart?: string;
}
export interface FiltresRegistre {
  page?: number;
  parPage?: number;
  numero?: string;
  du?: string;
  au?: string;
  vendeur?: string;
  artiste?: string;
  inventaire?: string;
}
export interface ListeRegistre {
  inscriptions: InscriptionPresentee[];
  pagination: Pagination;
}
export interface FicheInscription {
  inscription: InscriptionPresentee;
  rectifications: InscriptionPresentee[];
  sortie: InscriptionPresentee | null;
}
export interface VerificationRegistre {
  ok: boolean;
  verifiees: number;
  rupture?: { numero: number; cause: string };
}
const racine = (galerie: string, archive = false) =>
  `/g/${encodeURIComponent(galerie)}/${archive ? 'archive-registre-police' : 'registre-police'}`;
function parametres(filtres: FiltresRegistre): string {
  const params = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
  }
  return params.toString();
}
export const registrePoliceApi = {
  lister: (galerie: string, filtres: FiltresRegistre = {}, archive = false, appel?: AppelApi) =>
    api.get<ListeRegistre>(`${racine(galerie, archive)}?${parametres(filtres)}`, {
      galerie,
      ...appel,
    }),
  verifier: (galerie: string, archive = false, appel?: AppelApi) =>
    api.get<VerificationRegistre>(`${racine(galerie, archive)}/verification`, {
      galerie,
      ...appel,
    }),
  obtenir: (galerie: string, numero: number, appel?: AppelApi) =>
    api.get<FicheInscription>(`${racine(galerie)}/${numero}`, { galerie, ...appel }),
  propositions: (galerie: string, page = 1, appel?: AppelApi) =>
    api.get<{ propositions: PropositionPresentee[]; pagination: Pagination }>(
      `${racine(galerie)}/propositions?page=${page}&parPage=50`,
      { galerie, ...appel },
    ),
  proposition: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ proposition: PropositionPresentee }>(
      `${racine(galerie)}/propositions/${encodeURIComponent(id)}`,
      { galerie, ...appel },
    ),
  compte: (galerie: string, appel?: AppelApi) =>
    api.get<{ compte: number }>(`${racine(galerie)}/propositions/compte`, { galerie, ...appel }),
  creer: (galerie: string, brouillon: BrouillonRegistrePolice, appel?: AppelApi) =>
    api.post<{ proposition: PropositionPresentee }>(
      `${racine(galerie)}/propositions`,
      { brouillon },
      { galerie, ...appel },
    ),
  completer: (galerie: string, id: string, brouillon: BrouillonRegistrePolice, appel?: AppelApi) =>
    api.patch<{ proposition: PropositionPresentee }>(
      `${racine(galerie)}/propositions/${encodeURIComponent(id)}`,
      { brouillon },
      { galerie, ...appel },
    ),
  inscrire: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ inscription: InscriptionPresentee }>(
      `${racine(galerie)}/propositions/${encodeURIComponent(id)}/inscrire`,
      {},
      { galerie, ...appel },
    ),
  ecarter: (galerie: string, id: string, motif: string, appel?: AppelApi) =>
    api.post<{ proposition: PropositionPresentee }>(
      `${racine(galerie)}/propositions/${encodeURIComponent(id)}/ecarter`,
      { motif },
      { galerie, ...appel },
    ),
  rectifier: (
    galerie: string,
    numero: number,
    mentions: MentionsRegistrePolice,
    raison: string,
    appel?: AppelApi,
  ) =>
    api.post<{ inscription: InscriptionPresentee }>(
      `${racine(galerie)}/${numero}/rectifier`,
      { ...mentions, raison },
      { galerie, ...appel },
    ),
  sortir: (
    galerie: string,
    numero: number,
    sortie: { date: string; nature: 'vente' | 'restitution' | 'destruction'; factureRef?: string },
    appel?: AppelApi,
  ) =>
    api.post<{ inscription: InscriptionPresentee }>(`${racine(galerie)}/${numero}/sortie`, sortie, {
      galerie,
      ...appel,
    }),
  reglages: (galerie: string, appel?: AppelApi) =>
    api.get<{ inscrireDepots: boolean }>(`${racine(galerie)}/reglages`, { galerie, ...appel }),
  regler: (galerie: string, inscrireDepots: boolean, appel?: AppelApi) =>
    api.put<{ inscrireDepots: boolean }>(
      `${racine(galerie)}/reglages`,
      { inscrireDepots },
      { galerie, ...appel },
    ),
  deposerPiece: (galerie: string, fichier: File, appel?: AppelApi) => {
    const donnees = new FormData();
    donnees.append('fichier', fichier);
    return requeteMultipart<{ documentId: string }>(`${racine(galerie)}/pieces`, donnees, {
      galerie,
      ...appel,
    });
  },
  pdf: (
    galerie: string,
    periode: Pick<FiltresRegistre, 'du' | 'au'>,
    archive = false,
    appel?: AppelApi,
  ) =>
    api.get<{ url: string }>(`${racine(galerie, archive)}/export.pdf?${parametres(periode)}`, {
      galerie,
      ...appel,
    }),
  csv: (
    galerie: string,
    periode: Pick<FiltresRegistre, 'du' | 'au'>,
    archive = false,
    appel?: AppelApi,
  ) =>
    requeteTelechargement(`${racine(galerie, archive)}/export.csv?${parametres(periode)}`, {
      galerie,
      ...appel,
    }),
};
export const cleRegistrePolice = (galerie: string) => cleMetier(galerie, 'registre-police');
export function useListeRegistre(
  galerie: string,
  filtres: FiltresRegistre,
  archive = false,
  enabled = true,
) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'liste', archive, filtres],
    queryFn: (contexte) =>
      registrePoliceApi.lister(galerie, filtres, archive, appelDeRequete(contexte)),
    enabled,
  });
}
export function useVerificationRegistre(galerie: string, archive = false, enabled = true) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'verification', archive],
    queryFn: (contexte) => registrePoliceApi.verifier(galerie, archive, appelDeRequete(contexte)),
    enabled,
  });
}
export function usePropositionRegistre(galerie: string, id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'proposition', id],
    queryFn: (contexte) => registrePoliceApi.proposition(galerie, id!, appelDeRequete(contexte)),
    enabled: enabled && Boolean(id),
  });
}
export function useInscriptionRegistre(galerie: string, numero: number, enabled: boolean) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'inscription', numero],
    queryFn: (contexte) => registrePoliceApi.obtenir(galerie, numero, appelDeRequete(contexte)),
    enabled: enabled && Number.isInteger(numero) && numero > 0,
  });
}
export function useReglagesRegistre(galerie: string, enabled: boolean) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'reglages'],
    queryFn: (contexte) => registrePoliceApi.reglages(galerie, appelDeRequete(contexte)),
    enabled,
  });
}
export function usePropositionsRegistre(galerie: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'propositions', page],
    queryFn: (contexte) => registrePoliceApi.propositions(galerie, page, appelDeRequete(contexte)),
    enabled,
  });
}
export function useCompteRegistre(galerie: string, enabled: boolean) {
  return useQuery({
    queryKey: [...cleRegistrePolice(galerie), 'compte'],
    queryFn: (contexte) => registrePoliceApi.compte(galerie, appelDeRequete(contexte)),
    enabled,
  });
}
export function useMutationRegistre<T, V>(
  galerie: string,
  operation: (variables: V, appel: AppelApi) => Promise<T>,
) {
  const client = useQueryClient();
  return useMutation<T, Error, V>(
    dansLaPortee({
      mutationFn: operation,
      onSuccess: async () => {
        await client.invalidateQueries({ queryKey: cleRegistrePolice(galerie) });
        await client.invalidateQueries({ queryKey: cleMetier(galerie, 'tableau-de-bord') });
      },
    }),
  );
}
